import {z} from "zod";

export type SiteAccessibility = z.infer<typeof SiteAccessibility>;
export const SiteAccessibility = z.object({
	Monday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Tuesday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Wednesday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Thursday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Friday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Saturday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
	Sunday: z
		.object({
			from: z.string().optional(),
			until: z.string().optional(),
		})
		.optional(),
});

export type B2Cart = z.infer<typeof B2Cart>;
export const B2Cart = z.object({
	from: z.union([z.string(), z.undefined()]).optional(),
	until: z.union([z.string(), z.undefined()]).optional(),
	fromDateTime: z.union([z.string(), z.undefined()]).optional(),
	hours: z.union([z.number(), z.undefined()]).optional(),
	discountCode: z.union([z.string(), z.undefined()]).optional(),
	boxes: z.unknown(),
	name: z.union([z.string(), z.undefined()]).optional(),
	email: z.union([z.string(), z.undefined()]).optional(),
	phone: z.union([z.string(), z.undefined()]).optional(),
	locale: z.union([z.string(), z.undefined()]).optional(),
});

export type Branding = z.infer<typeof Branding>;
export const Branding = z.object({
	logoLightUrl: z.string().optional(),
	logoDarkUrl: z.string().optional(),
	logoPaddingX: z.number().optional(),
	logoPaddingY: z.number().optional(),
	colorPrimary: z.string().optional(),
	colorSecondary: z.string().optional(),
	muiThemeOptions: z.unknown().optional(),
});

export type B2Tenant = z.infer<typeof B2Tenant>;
export const B2Tenant = z.object({
	id: z.string(),
	name: z.string(),
	theme: z.union([z.unknown(), z.undefined()]).optional(),
	imprintUrl: z.union([z.string(), z.undefined()]).optional(),
	branding: z.union([Branding, z.undefined()]).optional(),
});

export type B2BoxType = z.infer<typeof B2BoxType>;
export const B2BoxType = z.object({
	type: z.string(),
	width: z.union([z.number(), z.undefined()]).optional(),
	height: z.union([z.number(), z.undefined()]).optional(),
	depth: z.union([z.number(), z.undefined()]).optional(),
	notes: z.union([z.string(), z.undefined()]).optional(),
	price: z.union([z.number(), z.undefined()]).optional(),
	packagePrice: z.union([z.unknown(), z.undefined()]).optional(),
	iconUrl: z.union([z.string(), z.undefined()]).optional(),
});

export type B2Site = z.infer<typeof B2Site>;
export const B2Site = z.object({
	id: z.string(),
	name: z.string(),
	street: z.union([z.string(), z.undefined()]).optional(),
	city: z.union([z.string(), z.undefined()]).optional(),
	country: z.union([z.string(), z.undefined()]).optional(),
	activeFrom: z.union([z.string(), z.undefined()]).optional(),
	activeUntil: z.union([z.string(), z.undefined()]).optional(),
	accessibleFrom: z.union([z.string(), z.undefined()]).optional(),
	accessibleUntil: z.union([z.string(), z.undefined()]).optional(),
	effectiveAccessibleFrom: z.union([z.string(), z.undefined()]).optional(),
	effectiveAccessibleUntil: z.union([z.string(), z.undefined()]).optional(),
	accessibility: SiteAccessibility,
	alwaysAccessible: z.boolean(),
	accessDescription: z.union([z.string(), z.undefined()]).optional(),
	externalAccess: z.union([z.literal("Storebox"), z.undefined()]).optional(),
	terms: z.union([z.string(), z.undefined()]).optional(),
	privacyPolicy: z.union([z.string(), z.undefined()]).optional(),
	optionalBookingFields: z
		.union([z.array(z.union([z.literal("name"), z.literal("phone"), z.literal("email")])), z.undefined()])
		.optional(),
	requiredBookingFields: z
		.union([z.array(z.union([z.literal("name"), z.literal("phone"), z.literal("email")])), z.undefined()])
		.optional(),
	boxes: z.array(B2BoxType),
	notBookableDates: z.union([z.array(z.string()), z.undefined()]).optional(),
	currency: z.union([z.literal("EUR"), z.literal("CHF"), z.undefined()]).optional(),
	bookingUnit: z.union([z.literal("Days"), z.literal("Hours"), z.undefined()]).optional(),
	pricingMode: z.union([z.literal("Unit"), z.literal("Package"), z.undefined()]).optional(),
	maxBookingUnits: z.union([z.number(), z.undefined()]).optional(),
	enableCubeQrCode: z.boolean(),
	enableRelease: z.boolean(),
	askReleaseLocker: z.boolean(),
	contactPhone: z.union([z.string(), z.undefined()]).optional(),
	automaticallySelectCubeId: z.union([z.string(), z.undefined()]).optional(),
	enabledAdvanceBooking: z.boolean(),
	enabledBookingEndAfter2ndOpening: z.boolean(),
	gracePeriodOf2ndOpening: z.union([z.number(), z.undefined()]).optional(),
	cancelUnpaidBookingAfter: z.union([z.number(), z.undefined()]).optional(),
	zoneId: z.union([z.string(), z.undefined()]).optional(),
});

export type B2Occupancy = z.infer<typeof B2Occupancy>;
export const B2Occupancy = z.object({
	uuid: z.string(),
	cubeDescription: z.union([z.string(), z.undefined()]).optional(),
	cubeId: z.union([z.string(), z.undefined()]).optional(),
	boxNumber: z.union([z.string(), z.undefined()]).optional(),
	lockStatus: z
		.union([z.literal("Open"), z.literal("Closed"), z.literal("Breakin"), z.literal("Blocked"), z.undefined()])
		.optional(),
	occupiedFrom: z.union([z.string(), z.undefined()]).optional(),
	openedTimes: z.union([z.number(), z.undefined()]).optional(),
});

export type B2BookingPayment = z.infer<typeof B2BookingPayment>;
export const B2BookingPayment = z.object({
	uuid: z.string(),
	createdAt: z.string(),
	amount: z.number(),
	paidAt: z.union([z.string(), z.undefined()]).optional(),
	paymentId: z.union([z.string(), z.undefined()]).optional(),
	paymentNote: z.union([z.string(), z.undefined()]).optional(),
	receiptText: z.union([z.string(), z.undefined()]).optional(),
});

export type B2Booking = z.infer<typeof B2Booking>;
export const B2Booking = z.object({
	id: z.string(),
	tenant: B2Tenant,
	site: B2Site,
	from: z.string(),
	until: z.string(),
	timezone: z.union([z.string(), z.undefined()]).optional(),
	prolongUntil: z.union([z.string(), z.undefined()]).optional(),
	createdAt: z.string(),
	paymentId: z.union([z.string(), z.undefined()]).optional(),
	paymentStatus: z.union([z.literal("open"), z.literal("paid"), z.undefined()]).optional(),
	discountCode: z.union([z.string(), z.undefined()]).optional(),
	boxes: z.unknown(),
	name: z.union([z.string(), z.undefined()]).optional(),
	email: z.union([z.string(), z.undefined()]).optional(),
	phone: z.union([z.string(), z.undefined()]).optional(),
	locale: z.union([z.string(), z.undefined()]).optional(),
	bookingCode: z.union([z.string(), z.undefined()]).optional(),
	receiptText: z.union([z.string(), z.undefined()]).optional(),
	totalAmount: z.union([z.number(), z.undefined()]).optional(),
	occupancies: z.union([z.array(B2Occupancy), z.undefined()]).optional(),
	payments: z.union([z.array(B2BookingPayment), z.undefined()]).optional(),
	siteHasMultipleCubes: z.union([z.boolean(), z.undefined()]).optional(),
	state: z.union([z.literal("Pending"), z.literal("Canceled"), z.literal("Confirmed"), z.undefined()]).optional(),
});

export type B2Prolong = z.infer<typeof B2Prolong>;
export const B2Prolong = z.object({
	until: z.string().optional(),
	hours: z.number().optional(),
	discountCode: z.string().optional(),
});

export type CubeRequest = z.infer<typeof CubeRequest>;
export const CubeRequest = z.object({
	cubeId: z.string(),
});

export type B2SiteSummary = z.infer<typeof B2SiteSummary>;
export const B2SiteSummary = z.object({
	id: z.string(),
	name: z.string(),
	street: z.union([z.string(), z.undefined()]).optional(),
	city: z.union([z.string(), z.undefined()]).optional(),
	country: z.union([z.string(), z.undefined()]).optional(),
	activeFrom: z.union([z.string(), z.undefined()]).optional(),
	activeUntil: z.union([z.string(), z.undefined()]).optional(),
	accessibility: SiteAccessibility,
	alwaysAccessible: z.boolean(),
});

export type SortObject = z.infer<typeof SortObject>;
export const SortObject = z.object({
	direction: z.string().optional(),
	nullHandling: z.string().optional(),
	ascending: z.boolean().optional(),
	property: z.string().optional(),
	ignoreCase: z.boolean().optional(),
});

export type PageableObject = z.infer<typeof PageableObject>;
export const PageableObject = z.object({
	offset: z.number().optional(),
	sort: z.array(SortObject).optional(),
	paged: z.boolean().optional(),
	unpaged: z.boolean().optional(),
	pageNumber: z.number().optional(),
	pageSize: z.number().optional(),
});

export type PageB2SiteSummary = z.infer<typeof PageB2SiteSummary>;
export const PageB2SiteSummary = z.object({
	totalPages: z.number().optional(),
	totalElements: z.number().optional(),
	first: z.boolean().optional(),
	last: z.boolean().optional(),
	size: z.number().optional(),
	content: z.array(B2SiteSummary).optional(),
	number: z.number().optional(),
	sort: z.array(SortObject).optional(),
	numberOfElements: z.number().optional(),
	pageable: PageableObject.optional(),
	empty: z.boolean().optional(),
});

export type B2Discount = z.infer<typeof B2Discount>;
export const B2Discount = z.object({
	code: z.string(),
	type: z.union([z.literal("Code"), z.literal("WestfieldLoyaltyService")]),
	value: z.number(),
});

export type post_CreateBooking = typeof post_CreateBooking;
export const post_CreateBooking = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/bookings"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			tenantId: z.string(),
			siteId: z.string(),
		}),
		body: B2Cart,
	}),
	response: B2Booking,
};

export type post_ReleaseLockers = typeof post_ReleaseLockers;
export const post_ReleaseLockers = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/release-lockers"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
	}),
	response: z.unknown(),
};

export type post_ProlongBooking = typeof post_ProlongBooking;
export const post_ProlongBooking = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/prolong"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
		body: B2Prolong,
	}),
	response: B2Booking,
};

export type post_OpenBox = typeof post_OpenBox;
export const post_OpenBox = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/open-box"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
		body: B2Occupancy,
	}),
	response: z.unknown(),
};

export type post_OccupyBoxes = typeof post_OccupyBoxes;
export const post_OccupyBoxes = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/occupy"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
		body: CubeRequest,
	}),
	response: z.array(B2Occupancy),
};

export type post_EndBooking = typeof post_EndBooking;
export const post_EndBooking = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/end"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
	}),
	response: z.unknown(),
};

export type post_VerifyCubeLocation = typeof post_VerifyCubeLocation;
export const post_VerifyCubeLocation = {
	method: z.literal("POST"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}/cube-verification"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
		body: CubeRequest,
	}),
	response: z.unknown(),
};

export type get_GetTenant = typeof get_GetTenant;
export const get_GetTenant = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			tenantId: z.string(),
		}),
	}),
	response: B2Tenant,
};

export type get_GetSites = typeof get_GetSites;
export const get_GetSites = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		query: z.object({
			page: z.number().optional(),
			size: z.number().optional(),
			sort: z.array(z.string()).optional(),
		}),
		path: z.object({
			tenantId: z.string(),
		}),
	}),
	response: PageB2SiteSummary,
};

export type get_GetSite = typeof get_GetSite;
export const get_GetSite = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites/{siteId}"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			tenantId: z.string(),
			siteId: z.string(),
		}),
	}),
	response: B2Site,
};

export type get_GetPricing = typeof get_GetPricing;
export const get_GetPricing = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/pricing"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		query: z.object({
			from: z.string().optional(),
			until: z.string().optional(),
			hours: z.number().optional(),
		}),
		path: z.object({
			tenantId: z.string(),
			siteId: z.string(),
		}),
	}),
	response: z.unknown(),
};

export type get_GetDiscount = typeof get_GetDiscount;
export const get_GetDiscount = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/discounts/{discountCode}"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			tenantId: z.string(),
			siteId: z.string(),
			discountCode: z.string(),
		}),
	}),
	response: B2Discount,
};

export type get_GetAvailability = typeof get_GetAvailability;
export const get_GetAvailability = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/availability"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		query: z.object({
			from: z.string().optional(),
			until: z.string().optional(),
			fromDateTime: z.string().optional(),
			hours: z.number().optional(),
		}),
		path: z.object({
			tenantId: z.string(),
			siteId: z.string(),
		}),
	}),
	response: z.unknown(),
};

export type get_GetBooking = typeof get_GetBooking;
export const get_GetBooking = {
	method: z.literal("GET"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
	}),
	response: B2Booking,
};

export type delete_CancelBooking = typeof delete_CancelBooking;
export const delete_CancelBooking = {
	method: z.literal("DELETE"),
	path: z.literal("/api/booking/v2/bookings/{bookingId}"),
	requestFormat: z.literal("json"),
	parameters: z.object({
		path: z.object({
			bookingId: z.string(),
		}),
	}),
	response: z.unknown(),
};

// <EndpointByMethod>
export const EndpointByMethod = {
	post: {
		"/api/booking/v2/tenants/{tenantId}/sites/{siteId}/bookings": post_CreateBooking,
		"/api/booking/v2/bookings/{bookingId}/release-lockers": post_ReleaseLockers,
		"/api/booking/v2/bookings/{bookingId}/prolong": post_ProlongBooking,
		"/api/booking/v2/bookings/{bookingId}/open-box": post_OpenBox,
		"/api/booking/v2/bookings/{bookingId}/occupy": post_OccupyBoxes,
		"/api/booking/v2/bookings/{bookingId}/end": post_EndBooking,
		"/api/booking/v2/bookings/{bookingId}/cube-verification": post_VerifyCubeLocation,
	},
	get: {
		"/api/booking/v2/tenants/{tenantId}": get_GetTenant,
		"/api/booking/v2/tenants/{tenantId}/sites": get_GetSites,
		"/api/booking/v2/tenants/{tenantId}/sites/{siteId}": get_GetSite,
		"/api/booking/v2/tenants/{tenantId}/sites/{siteId}/pricing": get_GetPricing,
		"/api/booking/v2/tenants/{tenantId}/sites/{siteId}/discounts/{discountCode}": get_GetDiscount,
		"/api/booking/v2/tenants/{tenantId}/sites/{siteId}/availability": get_GetAvailability,
		"/api/booking/v2/bookings/{bookingId}": get_GetBooking,
	},
	delete: {
		"/api/booking/v2/bookings/{bookingId}": delete_CancelBooking,
	},
};
export type EndpointByMethod = typeof EndpointByMethod;
// </EndpointByMethod>

// <EndpointByMethod.Shorthands>
export type PostEndpoints = EndpointByMethod["post"];
export type GetEndpoints = EndpointByMethod["get"];
export type DeleteEndpoints = EndpointByMethod["delete"];
export type AllEndpoints = EndpointByMethod[keyof EndpointByMethod];
// </EndpointByMethod.Shorthands>

// <ApiClientTypes>
export type EndpointParameters = {
	body?: unknown;
	query?: Record<string, unknown>;
	header?: Record<string, unknown>;
	path?: Record<string, unknown>;
};

export type MutationMethod = "post" | "put" | "patch" | "delete";
export type Method = "get" | "head" | MutationMethod;

type RequestFormat = "json" | "form-data" | "form-url" | "binary" | "text";

export type DefaultEndpoint = {
	parameters?: EndpointParameters | undefined;
	response: unknown;
};

export type Endpoint<TConfig extends DefaultEndpoint = DefaultEndpoint> = {
	operationId: string;
	method: Method;
	path: string;
	requestFormat: RequestFormat;
	parameters?: TConfig["parameters"];
	meta: {
		alias: string;
		hasParameters: boolean;
		areParametersRequired: boolean;
	};
	response: TConfig["response"];
};

type Fetcher = (
	method: Method,
	url: string,
	parameters?: EndpointParameters | undefined,
) => Promise<Endpoint["response"]>;

type RequiredKeys<T> = {
	[P in keyof T]-?: undefined extends T[P] ? never : P;
}[keyof T];

type MaybeOptionalArg<T> = RequiredKeys<T> extends never ? [config?: T] : [config: T];

// </ApiClientTypes>

// <ApiClient>
export class ApiClient {
	baseUrl: string = "";

	constructor(public fetcher: Fetcher) {}

	setBaseUrl(baseUrl: string) {
		this.baseUrl = baseUrl;
		return this;
	}

	// <ApiClient.post>
	post<Path extends keyof PostEndpoints, TEndpoint extends PostEndpoints[Path]>(
		path: Path,
		...params: MaybeOptionalArg<z.infer<TEndpoint["parameters"]>>
	): Promise<z.infer<TEndpoint["response"]>> {
		return this.fetcher("post", this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint["response"]>>;
	}
	// </ApiClient.post>

	// <ApiClient.get>
	get<Path extends keyof GetEndpoints, TEndpoint extends GetEndpoints[Path]>(
		path: Path,
		...params: MaybeOptionalArg<z.infer<TEndpoint["parameters"]>>
	): Promise<z.infer<TEndpoint["response"]>> {
		return this.fetcher("get", this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint["response"]>>;
	}
	// </ApiClient.get>

	// <ApiClient.delete>
	delete<Path extends keyof DeleteEndpoints, TEndpoint extends DeleteEndpoints[Path]>(
		path: Path,
		...params: MaybeOptionalArg<z.infer<TEndpoint["parameters"]>>
	): Promise<z.infer<TEndpoint["response"]>> {
		return this.fetcher("delete", this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint["response"]>>;
	}
	// </ApiClient.delete>
}

export function createApiClient(fetcher: Fetcher, baseUrl?: string) {
	return new ApiClient(fetcher).setBaseUrl(baseUrl ?? "");
}

/**
 Example usage:
 const api = createApiClient((method, url, params) =>
   fetch(url, { method, body: JSON.stringify(params) }).then((res) => res.json()),
 );
 api.get("/users").then((users) => console.log(users));
 api.post("/users", { body: { name: "John" } }).then((user) => console.log(user));
 api.put("/users/:id", { path: { id: 1 }, body: { name: "John" } }).then((user) => console.log(user));
*/

// </ApiClient
